// ═══════════════════════════════════════════════════════════════════════════
// Estado de la bitácora — Etapa 4b
//
// Dos piezas con propósitos distintos:
//
//   IndicadorBitacora — insignia en el encabezado. CALLADA cuando todo está
//     al día: un aviso permanente se vuelve parte del decorado y deja de
//     leerse. Solo aparece si hay algo pendiente o apartado.
//
//   PanelBitacora — detalle en Config. Lo importante aquí es que lo apartado
//     sea ACCIONABLE: ver qué falló, por qué, y poder reintentar o descartar.
//     Reportar sin ofrecer salida solo genera angustia.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { resumenBitacora, leerBitacora, reintentarFallidas,
         descartarFallidas } from "../datos/bitacora.js";
import { sincronizar, alCambiarBitacora } from "../datos/sincronizador.js";
import { btnSecondary } from "./estilos.js";

/** Se re-consulta la bitácora al cambiar y cada pocos segundos. */
function useBitacora() {
  const [resumen, setResumen] = useState(resumenBitacora);
  useEffect(() => {
    const refrescar = () => setResumen(resumenBitacora());
    const quitar = alCambiarBitacora(refrescar);
    const t = setInterval(refrescar, 5000);
    return () => { quitar?.(); clearInterval(t); };
  }, []);
  return resumen;
}

// ── Insignia del encabezado ────────────────────────────────────────────────

export function IndicadorBitacora({ onAbrir }) {
  const { pendientes, fallidas } = useBitacora();

  // Callada cuando no hay nada que decir.
  if (!pendientes && !fallidas) return null;

  const hayProblema = fallidas > 0;
  const texto = hayProblema
    ? `${fallidas} sin subir`
    : `${pendientes} por subir`;

  // Sin `onAbrir` la insignia solo informa: el detalle vive en Config y no todos
  // los roles entran ahí. Que se vea de dónde viene el aviso sirve igual; lo que
  // no sirve es un cursor de mano que no lleva a ningún lado.
  return (
    <div onClick={onAbrir || undefined}
      title={hayProblema
        ? "Hay cambios que no se pudieron subir." +
          (onAbrir ? " Toca para revisarlos." : " Avísale a quien administra la vinícola.")
        : "Cambios guardados aquí, esperando conexión para subir."}
      style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11,
        cursor: onAbrir ? "pointer" : "default", padding: "4px 10px", borderRadius: 6,
        background: hayProblema ? "#1f0d0d" : "#0d0d0d",
        border: `1px solid ${hayProblema ? "#3a1a1a" : "#2a2a1a"}`,
        color: hayProblema ? "#e8b0b0" : "#facc15" }}>
      <span>{hayProblema ? "⚠" : "☁"}</span>
      <span>{texto}</span>
    </div>
  );
}

// ── Panel de Config ────────────────────────────────────────────────────────

export function PanelBitacora() {
  const { pendientes, fallidas } = useBitacora();
  const [subiendo, setSubiendo] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [verDetalle, setVerDetalle] = useState(false);

  const alDia = !pendientes && !fallidas;
  const lista = verDetalle ? leerBitacora() : [];

  const subirAhora = async () => {
    setSubiendo(true); setMensaje("");
    const r = await sincronizar();
    setSubiendo(false);
    if (!r) setMensaje("No se pudo sincronizar.");
    else if (r.sinRed) setMensaje("Sin conexión. Se intentará cuando vuelva.");
    else setMensaje(`${r.subidas} cambio(s) subido(s).` +
      (r.fallidas ? ` ${r.fallidas} no se pudieron.` : ""));
  };

  return (
    <>
      <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>
        ☁ CAMBIOS POR SUBIR
      </div>

      {alDia ? (
        <div style={{ fontSize: 12, color: "#4ade80", marginBottom: 12 }}>
          Todo al día. No hay cambios esperando.
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 13, flexWrap: "wrap" }}>
          {pendientes > 0 && (
            <span style={{ color: "#facc15" }}>
              {pendientes} esperando conexión
            </span>
          )}
          {fallidas > 0 && (
            <span style={{ color: "#c97070" }}>
              {fallidas} no se pudieron subir
            </span>
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: "#666", lineHeight: 1.8, padding: "10px 12px",
        background: "#0a0a0a", borderRadius: 6, marginBottom: 12 }}>
        Tus cambios se guardan <b style={{ color: "#888" }}>siempre</b> en este dispositivo
        y se suben solos cuando hay conexión. No hace falta hacer nada.
        {fallidas > 0 && (
          <>
            <br />
            <span style={{ color: "#c9a96e" }}>
              Los que no se pudieron subir siguen guardados aquí: no se han perdido.
            </span>
          </>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={subirAhora} disabled={subiendo || alDia}
          style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12,
            opacity: (subiendo || alDia) ? 0.4 : 1 }}>
          {subiendo ? "Subiendo…" : "Subir ahora"}
        </button>

        {fallidas > 0 && (
          <button onClick={() => { reintentarFallidas(); subirAhora(); }}
            style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12,
              borderColor: "#3a3a1a", color: "#c9a96e" }}>
            Reintentar los {fallidas} fallidos
          </button>
        )}

        {(pendientes > 0 || fallidas > 0) && (
          <button onClick={() => setVerDetalle(v => !v)}
            style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
            {verDetalle ? "Ocultar detalle" : "Ver detalle"}
          </button>
        )}
      </div>

      {mensaje && (
        <div style={{ fontSize: 12, color: "#c9a96e", marginTop: 10 }}>{mensaje}</div>
      )}

      {verDetalle && lista.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {lista.map(m => (
            <div key={m.id} style={{ display: "flex", alignItems: "baseline", gap: 10,
              padding: "6px 0", borderBottom: "1px solid #161616", fontSize: 11 }}>
              <span style={{ color: m.estado === "fallida" ? "#c97070" : "#facc15", minWidth: 62 }}>
                {m.estado === "fallida" ? "no subió" : "esperando"}
              </span>
              <span style={{ color: "#888", minWidth: 108 }}>
                {m.tabla} · {m.operacion}
              </span>
              <span style={{ color: "#666", flex: 1 }}>
                {m.datos?.name || m.clave}
                {m.error && (
                  <span style={{ color: "#c97070", display: "block", marginTop: 2 }}>
                    {m.error}
                  </span>
                )}
              </span>
              <span style={{ color: "#444" }}>
                {new Date(m.creadoEn).toLocaleString("es-MX", { day: "2-digit",
                  month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          ))}

          {fallidas > 0 && (
            <button onClick={() => {
              if (!confirm(`¿Descartar ${fallidas} cambio(s) que no se pudieron subir?\n\n` +
                `Se pierden definitivamente. Los datos siguen en este dispositivo, ` +
                `pero dejarán de intentar llegar al servidor.`)) return;
              descartarFallidas();
            }} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 11, marginTop: 10,
              borderColor: "#3a1a1a", color: "#c97070" }}>
              Descartar los fallidos
            </button>
          )}
        </div>
      )}
    </>
  );
}
